






















































































































import { AlertBanner, FormBlock } from '@/app/components';
import store from '@/app/store';
import { useAxios } from '@/app/composable';
import { ChevronLeftIcon, DocumentTextIcon } from '@vue-hero-icons/outline';
import { computed, defineComponent, ref } from '@vue/composition-api';
import { TrinityRingsSpinner } from 'epic-spinners';
import * as R from 'ramda';
import { ValidationObserver } from 'vee-validate';
import { DerivationContractsAPI } from '../api';
import {
    AssetFields,
    ContractActions,
    ContractDetails,
    DerivationContractPreview,
    EthersConverter,
    LicenseDetails,
} from '../components';
import { useContractStatus } from '../composable';
import { ContractStatus, ContractTransactionStatus, DerivationStatus } from '../constants';
import { DerivationContract } from '../types';

export default defineComponent({
    name: 'DerivationContract',
    metaInfo: {
        title: 'Derivation Contract',
    },
    components: {
        ValidationObserver,
        TrinityRingsSpinner,
        DerivationContractPreview,
        ContractDetails,
        AssetFields,
        EthersConverter,
        FormBlock,
        ContractActions,
        LicenseDetails,
        DocumentTextIcon,
        ChevronLeftIcon,
        AlertBanner,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    setup(props, { root }) {
        const { exec, loading } = useAxios(true);
        const { statusClass } = useContractStatus();

        const contentRef = ref<any>(null);
        const contract = ref<DerivationContract | null>(null);
        const currentContract = ref<DerivationContract | null>(null);
        const user = ref<any>(store.state.auth.user);

        const negotiating = ref<boolean>(false);
        const isLegalRepresentative = ref<boolean>(user.value.roles.includes('legal-representative'));
        const acceptedRequest = ref<boolean>(false);

        const isSeller = computed(() => contract.value?.sellerOrgId === user.value?.organisationId);

        const isRequest = computed(
            () =>
                contract.value &&
                (!contract.value.status ||
                    (contract.value.status === DerivationStatus.Request && !acceptedRequest.value) ||
                    contract.value.status === DerivationStatus.Declined),
        );

        const onEditMode = computed(
            () => (contract.value?.status === DerivationStatus.Request && !isSeller.value) || negotiating.value,
        );

        const completedTransactions = computed(() =>
            contract.value?.transactions
                ? contract.value.transactions.filter((t) => t.status === ContractTransactionStatus.Completed)
                : [],
        );

        const sort = R.sortBy(R.prop('createdAt'));

        const previousNegotiation = computed(() =>
            completedTransactions.value.length > 1
                ? sort(completedTransactions.value)[completedTransactions.value.length - 2]
                : undefined,
        );

        const lastNegotiation = computed(() =>
            completedTransactions.value.length
                ? sort(completedTransactions.value)[completedTransactions.value.length - 1]
                : undefined,
        );

        // Returns if the current user is the creator of the last negotiation
        const isNegotiationCreator = computed(
            () => lastNegotiation.value?.changes?.metadata?.organisationId === user.value?.organisationId,
        );

        const offerContract = () => {
            currentContract.value = R.clone(contract.value);
            acceptedRequest.value = true;
        };

        const back = () => {
            if (window.history.length > 0) root.$router.go(-1);
            else root.$router.push({ name: 'contracts' });
        };

        const negotiate = () => {
            negotiating.value = true;
        };

        const cancel = () => {
            if (currentContract.value) {
                contract.value = currentContract.value;
                currentContract.value = null;
            }

            if (negotiating.value) negotiating.value = false;
            else if (acceptedRequest.value) acceptedRequest.value = false;
        };

        const scrollUp = () => {
            contentRef.value.scrollTo({ top: 0, behavior: 'smooth' });
        };

        const updateContract = (newContract: any) => {
            if (!contract.value) return;
            contract.value = newContract;
        };

        const getDerivationContract = () => {
            exec(DerivationContractsAPI.get(props.id)).then(async (res: any) => {
                contract.value = res.data;
            });
        };

        const sync = () => {
            if (!contract.value) return;
            exec(DerivationContractsAPI.syncContract(contract.value.id))
                .then((res: any) => {
                    const newContract = res.data;
                    if (!R.isNil(newContract.pendingTransactionId))
                        (root as any).$toastr.w(
                            'Blockchain transaction is still pending. Please try again later.',
                            'Pending Transaction',
                        );
                    else (root as any).$toastr.s('Contract state synchronised!', 'Success');
                    getDerivationContract();
                })
                .catch(() => {
                    (root as any).$toastr.e('Failed to synchronise contract state!', 'Error');
                });
        };

        getDerivationContract();

        return {
            contentRef,
            isSeller,
            isRequest,
            loading,
            contract,
            isLegalRepresentative,
            DerivationStatus,
            ContractStatus,
            previousNegotiation,
            lastNegotiation,
            onEditMode,
            negotiating,
            acceptedRequest,
            isNegotiationCreator,
            statusClass,
            offerContract,
            back,
            negotiate,
            cancel,
            scrollUp,
            sync,
            updateContract,
        };
    },
});
